import { PropertyFilterProperty } from '@amzn/awsui-collection-hooks';
import { TableProps } from '@amzn/awsui-components-react';
import { MultiAllocationDataSourceRecord } from 'src/models/multidimensional-allocation/MultiAllocationDataSourceRecord';
import CONSTANTS from 'src/utils/constants';

// Interface for the transformed data to display a table row.
export interface DataSourceRow {
    datasourceId: string;
    glAccount: string;
    costCenter: string;
    companyCode: string;
    location: string;
    project: string;
    channel: string;
    productLine: string;
    description: string;
    s3PresignUrl: string;
    executionId: string;
}

// Transform data source records to extract COA segment values
export const dataSourceRowTransformer = (records: MultiAllocationDataSourceRecord[]): DataSourceRow[] => {
    return records.map((record) => {
        // Helper function to extract first value from data key string
        const extractFirstValue = (dataKeyString?: string): string => {
            if (!dataKeyString) return '-';
            // Split by comma and take first value, then trim
            const firstValue = dataKeyString.split(',')[0]?.trim();
            return firstValue || '-';
        };
        // Extract COA segment values from dataKeyInput
        const dataKeyInput = record.dataKeyInput;

        return {
            executionId: record.lastBalancePullId!,
            datasourceId: record.datasourceId,
            s3PresignUrl: record.s3PresignUrl!,
            glAccount: extractFirstValue(dataKeyInput?.selectedAccounts),
            costCenter: extractFirstValue(dataKeyInput?.selectedCOA?.get(CONSTANTS.COA_SEGMENT_MAPPING.COST_CENTER.UI)),
            companyCode: extractFirstValue(dataKeyInput?.selectedCompanies),
            location: extractFirstValue(dataKeyInput?.selectedCOA?.get(CONSTANTS.COA_SEGMENT_MAPPING.LOCATION.UI)),
            project: extractFirstValue(dataKeyInput?.selectedCOA?.get(CONSTANTS.COA_SEGMENT_MAPPING.PROJECT.UI)),
            channel: extractFirstValue(dataKeyInput?.selectedCOA?.get(CONSTANTS.COA_SEGMENT_MAPPING.CHANNEL.UI)),
            productLine: extractFirstValue(
                dataKeyInput?.selectedCOA?.get(CONSTANTS.COA_SEGMENT_MAPPING.PRODUCT_LINE.UI)
            ),
            description: record.description || '-',
        };
    });
};

export const TABLE_COLUMNS: { [id: string]: { id: keyof DataSourceRow; label: string } } = {
    GL_ACCOUNT: {
        id: 'glAccount',
        label: 'GLAccount',
    },
    COST_CENTER: {
        id: 'costCenter',
        label: 'CostCenter',
    },
    COMPANY_CODE: {
        id: 'companyCode',
        label: 'CompanyCode',
    },
    LOCATION: {
        id: 'location',
        label: 'Location',
    },
    PROJECT: {
        id: 'project',
        label: 'Project',
    },
    CHANNEL: {
        id: 'channel',
        label: 'Channel',
    },
    PRODUCT_LINE: {
        id: 'productLine',
        label: 'ProductLine',
    },
    DESCRIPTION: {
        id: 'description',
        label: 'Description',
    },
};

// Column definitions for the table
export const COLUMN_DEFINITIONS: TableProps.ColumnDefinition<DataSourceRow>[] = Object.values(TABLE_COLUMNS).map(
    ({ id, label }) => ({ id: id, 
                          header: label, 
                          cell: (item) => item[id], 
                          sortingField: id 
                        })
);

export const FILTERING_PROPERTIES: PropertyFilterProperty[] = [
    {
        key: TABLE_COLUMNS.GL_ACCOUNT.id,
        operators: ['=', '!=', ':', '!:'],
        propertyLabel: TABLE_COLUMNS.GL_ACCOUNT.label,
        groupValuesLabel: 'GL Accounts',
    },
    {
        key: TABLE_COLUMNS.COST_CENTER.id,
        operators: ['=', '!=', ':', '!:'],
        propertyLabel: TABLE_COLUMNS.COST_CENTER.label,
        groupValuesLabel: 'Cost Centers',
    },
    {
        key: TABLE_COLUMNS.COMPANY_CODE.id,
        operators: ['=', '!=', ':', '!:'],
        propertyLabel: TABLE_COLUMNS.COMPANY_CODE.label,
        groupValuesLabel: 'Company Codes',
    },
    {
        key: TABLE_COLUMNS.LOCATION.id,
        operators: ['=', '!=', ':', '!:'],
        propertyLabel: TABLE_COLUMNS.LOCATION.label,
        groupValuesLabel: 'Locations',
    },
    {
        key: TABLE_COLUMNS.PROJECT.id,
        operators: ['=', '!=', ':', '!:'],
        propertyLabel: TABLE_COLUMNS.PROJECT.label,
        groupValuesLabel: 'Projects',
    },
    {
        key: TABLE_COLUMNS.CHANNEL.id,
        operators: ['=', '!=', ':', '!:'],
        propertyLabel: TABLE_COLUMNS.CHANNEL.label,
        groupValuesLabel: 'Channels',
    },
    {
        key: TABLE_COLUMNS.PRODUCT_LINE.id,
        operators: ['=', '!=', ':', '!:'],
        propertyLabel: TABLE_COLUMNS.PRODUCT_LINE.label,
        groupValuesLabel: 'Product Lines',
    },
    {
        key: TABLE_COLUMNS.DESCRIPTION.id,
        operators: [':', '!:'],
        propertyLabel: TABLE_COLUMNS.DESCRIPTION.label,
        groupValuesLabel: 'Description',
    },
];
